import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { EnumPipe } from '@core/pipes/enum.pipe';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ViewportService } from '@core/services/viewport.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { ECampaignStatus, EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICampaignExportApi } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { CreateCampaignDialogComponent } from '@pages/campaigns/create-campaign-dialog/create-campaign-dialog.component';
import { ICreateCampaignDialogData } from '@pages/campaigns/create-campaign-dialog/create-campaign-dialog.interface';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CampaignDetailsPlatformFeaturesService } from '@shared/components/profile-platform-feature-filter/profile-platform-feature-filter.service';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import { NgxFloatUiDirective, NgxFloatUiModule, NgxFloatUiTriggers } from 'ngx-float-ui';
import { QuillModule } from 'ngx-quill';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter } from 'rxjs';
import { CampaignDetailsBannerStore } from './campaign-details-banner.store';
import { CampaignExportButtonComponent } from './campaign-export-button/campaign-export-button.component';
import { CampaignExportDialogService } from './campaign-export-dialog/campaign-export-dialog.service';
import { ITokenUsageDialogData, TokenUsageDialogComponent } from './token-usage-dialog/token-usage-dialog.component';

@Component({
  imports: [
    MatButtonModule,
    HasPermissionDirective,
    BannerCardComponent,
    IconBadgeComponent,
    StatusBadgeComponent,
    TranslateModule,
    MatMenuModule,
    NgxFloatUiModule,
    QuillModule,
    FormsModule,
    EnumPipe,
    NgxSkeletonLoaderModule,
    CampaignExportButtonComponent,
    IconComponent,
    DatePipe,
    NgClass,
    ContentContainerComponent,
    NumberSuffixPipe,
    MatTooltipModule,
    TextClampTooltipDirective,
    DotsSpinnerComponent,
  ],
  providers: [CampaignDetailsBannerStore],
  selector: 'app-campaign-details-banner',
  templateUrl: './campaign-details-banner.component.html',
})
export class CampaignDetailsBannerComponent extends BaseComponent {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly breadcrumbs = input.required<IBreadcrumb[]>();
  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();

  public readonly adminMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);

  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly creatorMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);

  public readonly floatUiTriggerNone: NgxFloatUiTriggers = NgxFloatUiTriggers.none;

  public readonly platformFeatures = computed<EPlatformFeature[]>(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return [];
    }

    return Array.from(this.campaignDetailsPlatformFeaturesService.getCampaignPlatformFeatures(campaignAnalytics));
  });

  public readonly creators = computed(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return [];
    }

    return this.campaignExportDialogService.getCreators(campaignAnalytics);
  });

  public readonly reprocessEnabled = computed(() => {
    const campaign = this.campaign();

    if (!campaign) {
      return false;
    }

    return campaign.status !== ECampaignStatus.draft && campaign.status !== ECampaignStatus.planned;
  });

  public readonly campaignCreatorCost = computed(() => {
    const campaign = this.campaign();
    const cost = campaign?.campaignCreators && campaign.campaignCreators[0].cost;

    if (!campaign || !campaign.campaignCreators || !cost || !campaign.currency) {
      return null;
    }

    return this.numberSeparatorUtilitiesService.formatNumberToLocaleString(Number(cost), undefined, 0, 2, true);
  });

  public readonly store = inject(CampaignDetailsBannerStore);
  private readonly viewportService = inject(ViewportService);

  public readonly mobileBriefing = computed(() => this.creatorBriefingMode() && this.viewportService.isMobile());

  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly campaignsService = inject(CampaignsService);
  private readonly campaignExportDialogService = inject(CampaignExportDialogService);
  private readonly campaignDetailsPlatformFeaturesService = inject(CampaignDetailsPlatformFeaturesService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly numberSeparatorUtilitiesService = inject(NumberSeparatorUtilitiesService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly notesMenuFloatUi = viewChild('notesMenuFloatAnchor', { read: NgxFloatUiDirective });

  constructor() {
    super();

    this.createCreatedByUserEffect();
    this.createReprocessEffect();
  }

  public onEditClick(): void {
    const campaign = this.campaign();

    if (!campaign) {
      return;
    }

    this.eventTrackingService.track(EEventType.campaignCreationOpened, {
      campaignId: campaign.id,
      source: 'campaign_details_banner',
    });

    // adminMode makes the form load the campaign through the admin endpoint and point its
    // breadcrumb and back link at /admin/campaigns. Without it, an admin editing another group's
    // campaign from /admin/campaigns/:id gets "Campaign not found." and a "Campaigns" breadcrumb.
    this.router.navigate(['/campaigns/form', campaign.id], {
      state: { adminMode: this.adminMode(), fromCampaignDetails: true },
    });
  }

  public onDeleteClick(): void {
    const campaign = this.campaign();

    if (!campaign) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.campaigns.datatable.deleteCampaignMessage', {
          name: campaign.name,
        }),
        title: this.translate.instant('pages.campaigns.datatable.deleteCampaign'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.campaignsService.deleteCampaign(campaign.id).subscribe(() => {
          this.eventTrackingService.track(EEventType.campaignDeleted, {
            campaignId: campaign.id,
            source: 'campaign_details_banner',
          });
          this.router.navigate(['/campaigns']);
        });
      });
  }

  public onCustomerClick(): void {
    const campaign = this.campaign();

    if (this.creatorBriefingMode() || !campaign) {
      return;
    }

    WindowRedirectService.openUrlInNewTab(`/customers/${campaign.customer?.id}`);
  }

  public onCopyClick(): void {
    const campaign = this.campaign();

    if (!campaign) {
      return;
    }

    const copyDialogData: ICreateCampaignDialogData = {
      adminMode: this.adminMode(),
      mode: 'copy',
      sourceCampaign: {
        iconId: campaign.iconId ?? null,
        id: campaign.id,
        name: campaign.name,
      },
    };

    this.dialog.open(CreateCampaignDialogComponent, {
      autoFocus: true,
      data: copyDialogData,
      minWidth: '700px',
    });
  }

  public onNotesClick(): void {
    setTimeout(() => {
      const notesFloatUi = this.notesMenuFloatUi();

      if (!notesFloatUi) {
        return;
      }

      notesFloatUi.show();
    });
  }

  public onTokensClick(): void {
    const campaign = this.campaign();

    if (!campaign) {
      return;
    }

    this.dialog.open(TokenUsageDialogComponent, {
      data: {
        closeText: this.translate.instant('shared.close'),
        message: this.translate.instant('dialogs.campaignTokenUsage.message'),
        title: this.translate.instant('dialogs.campaignTokenUsage.title'),
        usageLabel: this.translate.instant('dialogs.campaignTokenUsage.usageLabel'),
        usageValue: campaign.tokenUsage ?? null,
      } satisfies ITokenUsageDialogData,
      width: '400px',
    });
  }

  public onReprocessClick(): void {
    const campaign = this.campaign();

    if (!campaign?.id || !this.reprocessEnabled()) {
      return;
    }

    this.store.reprocessCampaignAnalytics(campaign.id);
  }

  private createCreatedByUserEffect(): void {
    effect(() => {
      const campaign = this.campaign();

      if (!campaign || !campaign.createdBy) {
        return;
      }

      if (this.creatorBriefingMode() || this.exportMode()) {
        this.store.patchState({ createdByUser: null });

        return;
      }

      this.store.fetchCreatedByUser(campaign.createdBy);
    });
  }

  private createReprocessEffect(): void {
    effect(() => {
      const reprocessLoading = this.store.reprocessLoading$();
      const reprocessLoaded = this.store.reprocessLoaded$();
      const error = this.store.error$();

      if (!reprocessLoading && !reprocessLoaded && error) {
        this.snackbarService.openError(this.translate.instant('pages.campaignDetails.banner.reprocessError'));
      }

      if (!reprocessLoading && reprocessLoaded && !error) {
        this.snackbarService.openSuccess(this.translate.instant('pages.campaignDetails.banner.reprocessSuccess'));
      }
    });
  }
}
